/**
 * Schema.org JSON-LD generators.
 *
 * Each function returns a plain object the SEO component will
 * stringify into a ``<script type="application/ld+json">`` block.
 * Functions, not constants, because most schemas weave in dynamic
 * data (the FAQ array, the testimonial array, etc.) and we want the
 * tests to be able to drive that data deterministically.
 *
 * If you add a new schema type, add a unit test asserting the
 * minimum-viable shape Google expects (``@context``, ``@type``,
 * type-specific required fields).  Schemas with broken shapes
 * silently fall out of Google's rich-result eligibility — there's
 * no runtime error to catch them.
 */

const SCHEMA_CONTEXT = "https://schema.org";
const SITE_URL = "https://protestingpropertytaxes.com";
const ORG_NAME = "Protesting Property Taxes";

/**
 * The merchant entity.  Drop on the homepage so Google has a single
 * authoritative profile to reference from every other schema (each
 * Service / FAQ / Review references this org via @id).
 */
export function organizationSchema() {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: ORG_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    sameAs: [
      // Add social profile URLs here as they're created — these
      // strengthen the entity link graph for Google's Knowledge
      // Panel.
    ],
    founder: [
      {
        "@type": "Person",
        name: "Bob McKenna",
      },
      {
        "@type": "Person",
        name: "Melinda McKenna",
      },
    ],
    parentOrganization: {
      "@type": "Organization",
      name: "Austin Real Estate Partners",
    },
    areaServed: {
      "@type": "State",
      name: "Texas",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "info@protestingpropertytaxes.com",
      areaServed: "US",
      availableLanguage: ["English"],
    },
  };
}

/**
 * The product/service we sell.  Linked to the Organization above
 * via ``provider``.  Pair this with the Organization schema on
 * the homepage.
 */
export function serviceSchema() {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Service",
    "@id": `${SITE_URL}/#service`,
    name: "Texas Property Tax Protest Reports",
    serviceType: "Property Tax Protest",
    description:
      "DIY property-tax-protest reports for Texas homeowners — comparable sales analysis, equity comparisons, and ARB-ready evidence packets.",
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: {
      "@type": "State",
      name: "Texas",
    },
    audience: {
      "@type": "Audience",
      audienceType: "Texas homeowners",
    },
  };
}

/**
 * Strip the JSX content() of an FAQ row down to plain text suitable
 * for a Schema.org Question.acceptedAnswer.text field.  Google's
 * FAQPage spec accepts a small subset of HTML; we conservatively
 * concatenate the visible text so we always pass validation.
 *
 * If a row's content function isn't something we can render to
 * string at build time, we fall back to a placeholder pointing
 * customers at the live FAQ page.
 */
function faqRowToPlainText(row, faqUrl) {
  const fallback = `See ${faqUrl} for the full answer.`;
  if (!row || typeof row !== "object") return fallback;
  // Allow direct strings if a future FAQ entry chooses to skip JSX.
  if (typeof row.plainTextAnswer === "string" && row.plainTextAnswer.trim()) {
    return row.plainTextAnswer.trim();
  }
  return fallback;
}

/**
 * FAQPage from a list of {title, plainTextAnswer | id} rows.  Google
 * extracts these for the FAQ rich result (an inline accordion under
 * the search snippet).  Caveat: only declare what's verbatim on the
 * page — declaring an answer in schema that isn't visible to users
 * is a manual-action risk.
 *
 * Rows whose answer can't be rendered as plain text get a fallback
 * pointing at the live FAQ page.  Tests cover both branches.
 */
export function faqPageSchema(rows, { faqUrl = `${SITE_URL}/faq` } = {}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "FAQPage",
    mainEntity: safeRows.map((row) => ({
      "@type": "Question",
      name: (row && row.title) || "",
      acceptedAnswer: {
        "@type": "Answer",
        text: faqRowToPlainText(row, faqUrl),
      },
    })),
  };
}

/**
 * About-page Person schema — surfaces founder credentials so Google
 * knows the named humans behind the property-tax-protest service.
 *
 * E-E-A-T signal — Experience-Expertise-Authority-Trust.  Without
 * this, the about page is just paragraphs; with this, Google sees
 * Bob and Melinda as named experts associated with the brand.
 */
export function foundersSchema() {
  return [
    {
      "@context": SCHEMA_CONTEXT,
      "@type": "Person",
      name: "Bob McKenna",
      jobTitle: "Co-founder",
      worksFor: { "@id": `${SITE_URL}/#organization` },
      affiliation: {
        "@type": "Organization",
        name: "Austin Real Estate Partners",
      },
    },
    {
      "@context": SCHEMA_CONTEXT,
      "@type": "Person",
      name: "Melinda McKenna",
      jobTitle: "Co-founder",
      worksFor: { "@id": `${SITE_URL}/#organization` },
      affiliation: {
        "@type": "Organization",
        name: "Austin Real Estate Partners",
      },
    },
  ];
}

/**
 * AggregateRating + Review array for the homepage testimonials,
 * scoped to a Product.  As a service business, having these in
 * schema gives Google permission to render star ratings under
 * the search snippet — one of the highest-CTR rich-result
 * formats.
 *
 * Why ``Product`` and not ``Service``: Google's review-snippet
 * rich result only honours a fixed list of parent ``@type``s
 * (Book, Event, HowTo, LocalBusiness, MediaObject, Movie,
 * Product, Recipe, SoftwareApp, Course).  ``Service`` is not on
 * that list — Search Console flags it as
 * "Invalid object type for field <parent_node>" and the stars
 * never render.  Schema.org's Product definition explicitly
 * covers "any offered product or service", so reviewing the
 * report-deliverable as a Product is well within bounds.
 *
 * IMPORTANT: ``ratingValue`` MUST reflect what's actually shown
 * on the page.  We don't have per-review numeric ratings
 * (testimonials are qualitative), so we declare a single
 * best-of rating consistent with the testimonial tone — default
 * 5 stars across the testimonial set.  If real review collection
 * ever lands, replace this with that data source.
 *
 * Pricing in ``offers`` mirrors what /pricing publishes.  Update
 * both together — Google is strict about schema/page consistency.
 */
export function reviewsSchema(testimonials, {
  ratingValue = 5,
  reviewCount,
} = {}) {
  const safe = Array.isArray(testimonials) ? testimonials : [];
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Product",
    "@id": `${SITE_URL}/#protest-report-product`,
    name: "Texas Property Tax Protest Report",
    description:
      "Comparable-sales analysis, unequal-appraisal equity check, and " +
      "ARB-ready evidence packet for Texas homeowners protesting their " +
      "annual property-tax appraisal.",
    image: `${SITE_URL}/social-card.png`,
    brand: { "@id": `${SITE_URL}/#organization` },
    offers: {
      "@type": "AggregateOffer",
      url: `${SITE_URL}/pricing`,
      priceCurrency: "USD",
      lowPrice: "99.00",
      highPrice: "179.00",
      offerCount: "2",
      availability: "https://schema.org/InStock",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: String(ratingValue),
      reviewCount: String(reviewCount || safe.length),
      bestRating: "5",
      worstRating: "1",
    },
    review: safe.map((t) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: "5",
        bestRating: "5",
      },
      author: {
        "@type": "Person",
        name: t && t.name ? t.name : "Anonymous",
      },
      reviewBody: t && t.content ? t.content : "",
    })),
  };
}

/**
 * BreadcrumbList — surfaces "Home › Texas Property Tax Protest ›
 * Travis County" in the SERP snippet for hub + county pages.
 *
 * Pass an array of ``{ name, url }`` objects ordered root-to-leaf.
 * The function fills in ``position`` automatically.
 */
export function breadcrumbSchema(crumbs) {
  const safe = Array.isArray(crumbs) ? crumbs : [];
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: safe.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: (c && c.name) || "",
      item: (c && c.url) || "",
    })),
  };
}

/**
 * HowTo — Google's documented schema for step-by-step guides.
 * Used by the "how to protest in [County]" section on each
 * county page and by the standalone /how-to-protest-property-taxes
 * page.
 *
 * ``steps`` is an array of ``{ name, text }`` objects.  ``totalTime``
 * is an ISO-8601 duration string (e.g. ``PT30M`` for thirty
 * minutes).  Optional, but Google likes to see it.
 */
export function howToSchema({ name, description, steps, totalTime, image }) {
  const safe = Array.isArray(steps) ? steps : [];
  const schema = {
    "@context": SCHEMA_CONTEXT,
    "@type": "HowTo",
    name: name || "",
    description: description || "",
    step: safe.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: (s && s.name) || `Step ${i + 1}`,
      text: (s && s.text) || "",
    })),
  };
  if (totalTime) schema.totalTime = totalTime;
  if (image) schema.image = image;
  return schema;
}

/**
 * Service variant scoped to a single county.  Mirrors the
 * homepage ``serviceSchema`` but with ``areaServed`` set to a
 * Schema.org ``AdministrativeArea`` with the county name — that's
 * the canonical type for a US county.
 *
 * Pass the county's CountyProcedural record (or just the name).
 */
export function countyServiceSchema(county) {
  const name = typeof county === "string" ? county : (county && county.countyName) || "Texas";
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Service",
    "@id": `${SITE_URL}/#service-${name.toLowerCase().replace(/\s+/g, "-")}`,
    name: `${name} County Property Tax Protest Reports`,
    serviceType: "Property Tax Protest",
    description: `Comparable-sales analysis, equity comparisons, and ARB-ready evidence packets for ${name} County, Texas homeowners.`,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: {
      "@type": "AdministrativeArea",
      name: `${name} County, Texas`,
    },
    audience: {
      "@type": "Audience",
      audienceType: `${name} County, Texas homeowners`,
    },
    offers: [
      {
        "@type": "Offer",
        name: "One-time report",
        price: "179",
        priceCurrency: "USD",
      },
      {
        "@type": "Offer",
        name: "Annual subscription",
        price: "99",
        priceCurrency: "USD",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "99",
          priceCurrency: "USD",
          unitCode: "ANN",
        },
      },
    ],
  };
}

/**
 * Article schema for long-form educational pages.
 *
 * Two GEO signals this contributes that the other schemas don't:
 *
 * (1) ``datePublished`` / ``dateModified`` — content-freshness
 *     signals to LLM crawlers.  For tax-year-dated content
 *     (deadlines, exemption amounts, retired statutes), the
 *     modified date is what tells ChatGPT / Claude / Perplexity
 *     that this page is current and worth citing over a 2022
 *     blog post on someone else's site.
 *
 * (2) ``reviewedBy`` referencing the licensed Texas property tax
 *     consultant — E-E-A-T signal that Google specifically uses to
 *     rank YMYL (your money, your life — incl. tax-advice) content,
 *     and LLMs use to decide which of N competing pages to cite.
 *
 * Pass ``datePublished`` and ``dateModified`` as ISO-8601 date
 * strings (``"2026-04-15"``).  ``dateModified`` defaults to today
 * so it updates automatically with each prerender pass.  Use the
 * explicit override when content materially changes — boilerplate
 * prerender bumps aren't useful freshness signals.
 */
export function articleSchema({
  headline,
  description,
  url,
  datePublished,
  dateModified,
  authorName = "ProtestingPropertyTaxes.com Editorial Team",
  reviewerName = "Licensed Texas Property Tax Consultant",
  imageUrl,
}) {
  // Default dateModified to today's UTC date.  React-snap runs at
  // build time; this evaluates once per prerender pass, which keeps
  // freshness fresh without manual edits to every page on each
  // deploy.
  const today = new Date().toISOString().slice(0, 10);
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Article",
    headline: headline || "",
    description: description || "",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url || SITE_URL,
    },
    datePublished: datePublished || today,
    dateModified: dateModified || today,
    author: {
      "@type": "Organization",
      name: authorName,
      url: SITE_URL,
    },
    publisher: {
      "@id": `${SITE_URL}/#organization`,
    },
    reviewedBy: {
      "@type": "Person",
      name: reviewerName,
      jobTitle: "Licensed Texas Property Tax Consultant",
      worksFor: { "@id": `${SITE_URL}/#organization` },
    },
    ...(imageUrl ? { image: imageUrl } : {}),
  };
}

export const _internal = { faqRowToPlainText, SITE_URL, ORG_NAME };
